import Vue from 'vue'
import apiConfig from '@core/app-config/api-config'
import maintenanceApi from '@/services/maintenance-api.service'
import settingsApi from '@/services/settings-api.service'

const IDLE_MS = 15000
/** While the edge router has not yet applied a change: the banner and the button follow it within seconds */
const PENDING_MS = 3000

/**
 * One copy of GET /maintenance for the banner on every page and the «Заглушка» page, so a save on the page shows up in
 * the banner at once, without a reload.
 */
export const maintenanceState = Vue.observable({
  overview: null,
  error: '',
  /** the manual flag is being saved: 'on' | 'off' | null */
  saving: null,
  /**
   * A switch of the manual flag on its way to visitors: { target: 'on' | 'off', since }. Cleared once the edge routers
   * report it applied; then `applied` gets { target, seconds } for a toast (the banner shows it on any page).
   */
  applying: null,
  applied: null,
  /** the switch took longer than expected: { target, seconds } */
  slow: null,
})

let subscribers = 0
let timer = null

const MANUAL_REASON = 'включена вручную'

/**
 * What the settings ask for against what visitors see now. Edge routers apply a change within one check interval,
 * so for a while the two differ: `pending` is 'on' or 'off' then.
 */
export function describeMaintenance(overview) {
  if (!overview) return { known: false }
  const { config } = overview
  const live = overview.guards.filter(guard => !guard.lost)
  const visible = live.filter(guard => guard.active)
  let pending = null
  if (config.manual && live.length && !visible.length) pending = 'on'
  else if (!config.manual && visible.some(guard => (guard.reason || '').startsWith(MANUAL_REASON))) pending = 'off'

  return {
    known: true,
    manual: config.manual,
    intervalSeconds: config.intervalSeconds,
    /** no edge router reports: nobody knows what visitors see */
    noReports: !live.length,
    visible,
    pending,
    bypassConfigured: overview.bypassConfigured,
  }
}

/** Three check intervals and a margin: past that, a switch is reported as slow */
const slowAfterMs = overview => ((overview?.config.intervalSeconds || 15) * 3 + 10) * 1000

function trackApplying() {
  const { applying, overview } = maintenanceState
  if (!applying || !overview) return
  const state = describeMaintenance(overview)
  const seconds = Math.round((Date.now() - applying.since) / 1000)
  if (state.noReports) {
    // nobody can confirm it: the status line says so
    maintenanceState.applying = null
  } else if (!state.pending) {
    maintenanceState.applying = null
    // `reasons`: what still holds the page after the flag was removed (a failing check with the auto switch on)
    maintenanceState.applied = { target: applying.target, seconds, reasons: state.visible.map(guard => guard.reason).filter(Boolean) }
  } else if (!applying.slowReported && Date.now() - applying.since > slowAfterMs(overview)) {
    applying.slowReported = true
    maintenanceState.slow = { target: applying.target, seconds }
  }
}

export async function refreshMaintenance() {
  try {
    const res = await maintenanceApi.getOverview()
    maintenanceState.overview = res.data
    maintenanceState.error = ''
    trackApplying()
  } catch (e) {
    maintenanceState.error = e?.response?.data?.message || 'Не удалось загрузить состояние заглушки'
  }
  clearTimeout(timer)
  if (subscribers) {
    const fast = maintenanceState.applying || describeMaintenance(maintenanceState.overview).pending
    timer = setTimeout(refreshMaintenance, fast ? PENDING_MS : IDLE_MS)
  }
}

/** Polls while anyone shows the state; returns the unsubscribe function */
export function subscribeMaintenance() {
  subscribers += 1
  if (subscribers === 1) refreshMaintenance()
  return () => {
    subscribers -= 1
    if (!subscribers) clearTimeout(timer)
  }
}

/** The manual flag was changed (by the button or the switch in the settings form): follow it to the visitors */
export function startApplying(value) {
  maintenanceState.applying = { target: value ? 'on' : 'off', since: Date.now(), slowReported: false }
  maintenanceState.applied = null
  maintenanceState.slow = null
  return refreshMaintenance()
}

/**
 * Saves the manual flag. The state flips at once (the page and the banner react before the request returns) and goes
 * back if the save fails; then it follows the edge routers until they apply it.
 */
export async function setManualMaintenance(value) {
  const { overview } = maintenanceState
  const before = overview ? overview.config.manual : null
  if (overview) overview.config.manual = value
  maintenanceState.saving = value ? 'on' : 'off'
  try {
    await settingsApi.update('maintenance.manual', value)
  } catch (e) {
    if (overview && maintenanceState.overview === overview) overview.config.manual = before
    throw new Error(e?.response?.data?.message || 'Не удалось сохранить')
  } finally {
    maintenanceState.saving = null
  }
  await startApplying(value)
}

/**
 * Opens the public site past the maintenance page: a one-minute token from the backend, which the site's edge router
 * swaps for its own 2-hour cookie. Throws the backend error message (e.g. no MAINTENANCE_BYPASS_SECRET).
 */
export async function openBehindMaintenance() {
  // Opened before the request: a window opened after an await is taken for a popup and blocked
  const win = window.open('about:blank', '_blank')
  try {
    const res = await maintenanceApi.issueBypass()
    const url = `${apiConfig.clientSiteHost.replace(/\/+$/, '')}/__maintenance/enter?t=${encodeURIComponent(res.data.token)}`
    if (win) win.location.href = url
    else window.location.href = url
  } catch (e) {
    if (win) win.close()
    throw new Error(e?.response?.data?.message || 'Не удалось получить ссылку')
  }
}
